// Blazor.start() never rejects on a failed boot; the failure only arrives here as an
// unhandled "Failed to start platform" rejection.
export const BOOT_FAILURE_FINGERPRINT = 'blazor-boot-failure';
const BOOT_FAILURE = /^Failed to start platform\./;

export const SERVICE_WORKER_FAILURE_FINGERPRINT = 'service-worker-registration-failed';

export const ERROR_ORIGIN_TAG = 'error.origin';
export const DOTNET_INTEROP_ORIGIN = 'dotnet-interop';

const UNHANDLED_NOISE_PATTERNS = [
  /^Failed to load config file .*blazor\.boot\.json/,
  /^download '.+' for .+ failed/,
  /^Load failed$/,
  /^Failed to fetch$/,
  /^NetworkError when attempting to fetch resource\.?$/,
  /^Importing a module script failed\.?$/,
  /^error loading dynamically imported module/,
];

const THIRD_PARTY_SCRIPT_LOAD = /^Failed to load (https?:\/\/\S+)/;

export function toSentryEnvironment(webpackMode) {
  return webpackMode === 'production' ? 'Production' : 'Development';
}

export function toSentryRelease(version) {
  const match = /^(\d+)\.(\d+)\.(\d+)/.exec(version ?? '');
  return match ? `${match[1]}.${match[2]}.${match[3]}` : undefined;
}

const exceptionValues = (event) => event?.exception?.values ?? [];

const frames = (event) =>
  exceptionValues(event).flatMap((value) => value?.stacktrace?.frames ?? []);

const messages = (event) =>
  [...exceptionValues(event).map((value) => value?.value), event?.message]
    .filter((message) => typeof message === 'string');

const originOf = (url) => {
  try {
    return new URL(url).origin;
  } catch {
    return null;
  }
};

export function isUnhandled(event) {
  return exceptionValues(event).some((value) => value?.mechanism?.handled === false);
}

export function isUnhandledNoise(event, origin) {
  return messages(event).some((message) => {
    if (UNHANDLED_NOISE_PATTERNS.some((pattern) => pattern.test(message))) return true;

    const scriptLoad = THIRD_PARTY_SCRIPT_LOAD.exec(message);
    return scriptLoad !== null && originOf(scriptLoad[1]) !== origin;
  });
}

export function isDotNetInteropError(event) {
  return frames(event).some((frame) => /DotNet/.test(frame?.function ?? ''));
}

export function isBootFailure(event) {
  return messages(event).some((message) => BOOT_FAILURE.test(message));
}

export function createBeforeSend({ origin }) {
  return (event) => {
    if (isBootFailure(event)) {
      event.fingerprint = [BOOT_FAILURE_FINGERPRINT];
      event.level = 'warning';
      return event;
    }

    if (isUnhandled(event) && isUnhandledNoise(event, origin)) return null;

    if (isDotNetInteropError(event))
      event.tags = { ...event.tags, [ERROR_ORIGIN_TAG]: DOTNET_INTEROP_ORIGIN };

    return event;
  };
}
